import { logger } from '@damofire/data-commons';
import { clone } from 'lodash-es';
import { Observable, Observer, Subject } from 'rxjs';
import {
  WAREHOUSE_SYNC_STEP_PUSH,
  WarehouseSyncStep,
  WarehouseSyncStepContext,
  WarehouseSyncStepContextEvent,
  WarehouseSyncStepParams,
  WarehouseSyncStepTaskContext,
} from '../executors';
import { WarehouseSyncStepStatusListenerProxy } from '../warehouse-sync-listener';
import { WarehouseSyncStepExecutor } from './warehouse-steps.executor';

export class WarehouseSyncExecutor {
  private shouldCancel = false;
  private readonly syncStatusEvents: Subject<WarehouseSyncStepContextEvent> = new Subject<WarehouseSyncStepContextEvent>();

  constructor(private readonly stepExecutor: WarehouseSyncStepExecutor) {}

  execute(params: WarehouseSyncStepParams): Observable<WarehouseSyncStepContextEvent> {
    return this.fromScope(params);
  }

  executeSingleTask(params: WarehouseSyncStepParams, step: WarehouseSyncStep, taskName: string): Observable<WarehouseSyncStepContextEvent> {
    return this.executeSingleTasks(params, step, [taskName]);
  }

  executeSingleTasks(params: WarehouseSyncStepParams, step: WarehouseSyncStep, taskNames: string[]): Observable<WarehouseSyncStepContextEvent> {
    logger.info('Starting single-task sync executor', { category: 'sync', division: params.division, step, tasks: taskNames });
    const listenerProxy = new WarehouseSyncStepStatusListenerProxy(this.syncStatusEvents);
    const syncId = `${Date.now()}`;
    this.stepExecutor
      .executeTasks(params, syncId, step, taskNames, listenerProxy)
      .then((ctx) => {
        ctx.when = Date.now();
        if (this.shouldCancel) {
          listenerProxy.next({ ctx: clone(ctx), event: 'SYNC_CANCELLED' });
        } else {
          listenerProxy.next({ ctx: clone(ctx), event: 'SYNC_FINISHED' });
        }
      })
      .catch((err) => logger.error('executeSingleTasks unhandled error', { category: 'sync', error: err }));
    return this.syncStatusEvents.asObservable();
  }

  cancelSync(): void {
    logger.info('Sync cancel requested', { category: 'sync' });
    this.shouldCancel = true;
    this.stepExecutor.cancelSync();
  }
  private fromScope(params: WarehouseSyncStepParams): Observable<WarehouseSyncStepContextEvent> {
    logger.info('Starting sync executor', { category: 'sync', division: params.division });
    const listenerProxy = new WarehouseSyncStepStatusListenerProxy(this.syncStatusEvents);
    const syncId = `${new Date().getTime()}`;
    this.executeStepsStartingAt(params, syncId, WAREHOUSE_SYNC_STEP_PUSH, listenerProxy).catch((err) =>
      logger.error('fromScope unhandled error', { category: 'sync', error: err }),
    );
    return this.syncStatusEvents.asObservable();
  }

  private async executeStepsStartingAt(params: WarehouseSyncStepParams, syncId: string, step: WarehouseSyncStep, listener: Observer<WarehouseSyncStepContextEvent>): Promise<void> {
    const { steps, division } = params;
    let ctx: WarehouseSyncStepContext | WarehouseSyncStepTaskContext;
    let currentStep = step;

    // Run exactly the requested steps. (Previously this looped from PUSH up to
    // the max requested step, so asking for CLEAN alone silently ran the full
    // push+download+clean pipeline.)
    for (currentStep of [...steps].sort((a, b) => a - b)) {
      if (this.shouldCancel) {
        break;
      }
      try {
        logger.info(`Executing sync step ${currentStep}`, { category: 'sync', division, syncId });
        ctx = await this.stepExecutor.executeStep(params, syncId, currentStep, listener);
      } catch (err) {
        logger.error('executeStepsStartingAt error', { category: 'sync', step: currentStep, division, error: err });
      }
    }

    if (ctx) {
      ctx.when = new Date().getTime();
      if (!this.shouldCancel) {
        listener.next({ ctx: clone(ctx), event: 'SYNC_FINISHED' });
      } else {
        listener.next({ ctx: clone(ctx), event: 'SYNC_CANCELLED' });
      }
    } else {
      if (!this.shouldCancel) {
        logger.error('Sync steps did not finish properly', { category: 'sync', division });
        listener.next({ ctx: { syncId, division, step: currentStep }, event: 'SYNC_FINISHED' });
      } else {
        logger.error('Sync steps did not finish properly (cancelled)', { category: 'sync', division });
        listener.next({ ctx: { syncId, division, step: currentStep }, event: 'SYNC_CANCELLED' });
      }
    }
  }
}
